import { DepotDto } from "@/common/types/DepotDto";
import { AsyncState } from "@/common/store/util/AsyncCall";
import { ActionTree, GetterTree, ModuleTree, MutationTree } from "vuex";
import axios from 'axios';
import { PsbUser } from "@/common/types/PsbUser";
import {
  ACCOUNT_CREATED,
  CONNECTION_REQUESTED,
  INVALID_ACCOUNT,
  INVALID_ADDRESS,
  INVALID_NAME,
  INVALID_STATE,
  LinkRequest, LinkRequestStatus,
  REQUEST_ACCEPTED, REQUEST_DECLINED
} from "@/common/types/BrokerLinks";
import {
  API_CALL_ERROR,
  API_CALL_PENDING,
  API_CALL_SUCCESSFUL,
  ApiState,
  PAGE_SIZE_MAX
} from "@/common/store/util/ApiStates";
import { PlacedOrderDto } from "@/common/types/PlacedOrderDto";
import { RootStore } from "@/common/store";
import { TradingServer } from '@/utils/Api';

type RootState = RootStore;

export interface DepotFetchParameter {
  id: string
}

export interface PsbUserFetchParameter {
  email: string
}

export interface PlacedOrdersFetchParameter {
  id: string
}

export interface LinkRequestFetchParameter {
  requesterId: string
}

export interface LinkRequestUpdateParameter {
  linkRequest: string,
  targetStatus: LinkRequestStatus
}

// TODO: Refactor the AsyncState in a way, that it has a ApiState for Fetch, Update and create..
// or a variable amount of api calls
export type DepotStore = {
  depot: AsyncState<DepotDto, DepotFetchParameter>,
  psbUser: AsyncState<PsbUser[], PsbUserFetchParameter>,
  linkRequests: AsyncState<LinkRequest[], LinkRequestFetchParameter>,
  linkRequestsUpdateState: ApiState,
  placedOrders: AsyncState<PlacedOrderDto[], PlacedOrdersFetchParameter>
}

let depotState = new AsyncState<DepotDto, DepotFetchParameter>('depot',
  {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    zipCode: '',
    city: '',
    countryCode: '',
    linkStatus: 'CREATED',
    lastInteraction: '',
    account: ''
  },
  { id: '' });

let psbUserState = new AsyncState<PsbUser[], PsbUserFetchParameter>('psbUser', [], { email: '' });

let linkRequestState = new AsyncState<LinkRequest[], LinkRequestFetchParameter>('linkRequests', [], { requesterId: '' });

let placedOrdersState = new AsyncState<PlacedOrderDto[], PlacedOrdersFetchParameter>('placedOrders', [], { id: '' });

const state: DepotStore = {
  depot: depotState,
  psbUser: psbUserState,
  linkRequests: linkRequestState,
  linkRequestsUpdateState: "",
  placedOrders: placedOrdersState
};

const getters: GetterTree<DepotStore, RootState> = {
  ...depotState.defaultGetters<DepotStore>(),
  ...psbUserState.defaultGetters<DepotStore>(),
  ...linkRequestState.defaultGetters<DepotStore>(),
  ...placedOrdersState.defaultGetters<DepotStore>(),
  LINKREQUEST_IS_PENDING: (store: DepotStore) => {

    if (!store.linkRequests.data.length) {
      return false;
    }

    let latestItem = store.linkRequests.data[0];

    return !!(latestItem.status != REQUEST_DECLINED &&
      latestItem.status != REQUEST_ACCEPTED &&
      latestItem.status != ACCOUNT_CREATED);
  },
  LINKREQUEST_IS_CREATED: (store: DepotStore) => {

    if (!store.linkRequests.data.length) {
      return false;
    }

    let latestItem = store.linkRequests.data[0];

    return !!(latestItem.status == ACCOUNT_CREATED);
  },
  LINKREQUEST_IS_DECLINED: (store: DepotStore) => {

    if (!store.linkRequests.data.length) {
      return false;
    }

    let latestItem = store.linkRequests.data[0];

    return !!(latestItem.status == REQUEST_DECLINED);
  },
  LINKREQUEST_IS_SAVING: (store: DepotStore) => store.linkRequestsUpdateState === API_CALL_PENDING,
  LINKREQUEST_SAVED: (store: DepotStore) => store.linkRequestsUpdateState === API_CALL_SUCCESSFUL
};

const actions: ActionTree<DepotStore, RootState> = {
  [depotState.getConstants().FETCH]: (store, requestParam: DepotFetchParameter) => {
    const broker = store.rootState.auth.broker;

    if (broker) {
      depotState.asyncApiCall(
        store,
        axios.get(process.env.VUE_APP_API_URL + '/v2/depots/' + broker + '/owners/' + requestParam.id),
        requestParam
      );
    }
  },
  [psbUserState.getConstants().FETCH]: (store, requestParam: PsbUserFetchParameter) => {
    psbUserState.asyncApiCall(
      store,
      axios.get(process.env.VUE_APP_API_URL + '/v1/psb/users', { params: { email: requestParam.email } }),
      requestParam
    );
  },
  [placedOrdersState.getConstants().FETCH]: (store, requestParam: PlacedOrdersFetchParameter) => {
    const broker = store.rootState.auth.broker;

    if (broker) {
      placedOrdersState.asyncApiCall(
        store,
        axios.get(process.env.VUE_APP_API_URL + '/v2/depots/' + broker + '/owners/' + requestParam.id + '/placed-orders'),
        requestParam,
        function (response: any) {
          return response.data.sort(
            (a: PlacedOrderDto, b: PlacedOrderDto) => {
              if (a.transmissionDate < b.transmissionDate) {
                return 1;
              }
              if (a.transmissionDate > b.transmissionDate) {
                return -1;
              }
              return 0;
            }
          );
        }
      );
    }
  },
  UPDATE_LINKREQUEST: (store, requestParam: LinkRequestUpdateParameter) => {

    store.commit('UPDATE_LINKREQUEST_CALLED');
    const broker = store.rootState.auth.broker;

    TradingServer({
      method: 'PUT',
      url: '/v1/broker-accounts/{broker}/applications/{account}',
      urlParams: { broker: broker + "", account: requestParam.linkRequest },
      data: { status: requestParam.targetStatus }
    })
      .then(resp => {
        store.commit('UPDATE_LINKREQUEST_SUCCESSFUL', { response: resp.data } );
      })
      .catch(err => {
        store.commit('UPDATE_LINKREQUEST_FAILED');

      });
  },
  [linkRequestState.getConstants().FETCH]: (store, requestParam: LinkRequestFetchParameter) => {
    const broker = store.rootState.auth.broker;

    linkRequestState.asyncApiCall(
      store,
      axios.get(process.env.VUE_APP_API_URL + '/v1/brokeraccountlinkrequest', { params: {
        requesterId: requestParam.requesterId,
        broker: broker,
        size: PAGE_SIZE_MAX
      } }),
      requestParam,
      function(response: any) {

        let result = [];

        for (const linkRequest of response.data.content) {
          if (linkRequest.initiation === 'CREATE') {
            result.push({
              id: linkRequest.id,
              broker: linkRequest.brokerType,
              date: linkRequest.created,
              status: ACCOUNT_CREATED,
              credential: linkRequest.userBrokerCredential
            } as LinkRequest);
          } else {
            result.push({
              id: linkRequest.id,
              broker: linkRequest.brokerType,
              date: linkRequest.created,
              status: CONNECTION_REQUESTED,
              accountNo: linkRequest.account,
              credential: linkRequest.userBrokerCredential
            } as LinkRequest);

            if (linkRequest.accepted || linkRequest.status === 'ACCEPTED') {
              result.push({
                id: linkRequest.id,
                broker: linkRequest.brokerType,
                date: linkRequest.accepted,
                status: REQUEST_ACCEPTED,
                accountNo: linkRequest.account,
                credential: linkRequest.userBrokerCredential
              } as LinkRequest);
            } else {
              if (linkRequest.updated) {
                if (linkRequest.status === 'DECLINED') {
                  result.push({
                    id: linkRequest.id,
                    broker: linkRequest.brokerType,
                    date: linkRequest.updated,
                    status: REQUEST_DECLINED,
                    accountNo: linkRequest.account,
                    credential: linkRequest.userBrokerCredential
                  } as LinkRequest);
                } else if ([ INVALID_ACCOUNT, INVALID_ADDRESS, INVALID_NAME, INVALID_STATE ].includes(linkRequest.status)) {
                  result.push({
                    id: linkRequest.id,
                    broker: linkRequest.brokerType,
                    date: linkRequest.updated,
                    status: linkRequest.status,
                    accountNo: linkRequest.account,
                    credential: linkRequest.userBrokerCredential
                  } as LinkRequest);
                }
              }
            }
          }
        }
        result.sort((a: LinkRequest, b: LinkRequest) => {
          if (a.date < b.date) {
            return 1;
          }
          if (a.date > b.date) {
            return -1;
          }
          return 0;
        });

        return result;
      }
    );
  }
};

const mutations: MutationTree<DepotStore> = {
  ...depotState.defaultMutations<DepotStore>(),
  ...psbUserState.defaultMutations<DepotStore>(),
  ...linkRequestState.defaultMutations<DepotStore>(),
  ...placedOrdersState.defaultMutations<DepotStore>(),
  UPDATE_LINKREQUEST_CALLED: (store: DepotStore) => {
    store.linkRequestsUpdateState = API_CALL_PENDING;
  },
  UPDATE_LINKREQUEST_SUCCESSFUL: (store: DepotStore) => {
    store.linkRequestsUpdateState = API_CALL_SUCCESSFUL;
  },
  UPDATE_LINKREQUEST_FAILED: (store: DepotStore) => {
    store.linkRequestsUpdateState = API_CALL_ERROR;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
} as ModuleTree<DepotStore>;

export const DEPOT_STORE = 'depot';

export const FETCH_DEPOT = DEPOT_STORE + '/' + depotState.getConstants().FETCH;
export const DEPOT_LOADING = depotState.getConstants().LOADING;
export const DEPOT_LOADED = depotState.getConstants().LOADED;
export const DEPOT = depotState.getConstants().DATA;

export const FETCH_PSBUSER = DEPOT_STORE + '/' + psbUserState.getConstants().FETCH;
export const PSBUSER_LOADING = psbUserState.getConstants().LOADING;
export const PSBUSER_LOADED = psbUserState.getConstants().LOADED;
export const PSBUSER = psbUserState.getConstants().DATA;

export const FETCH_LINKREQUESTS = DEPOT_STORE + '/' + linkRequestState.getConstants().FETCH;
export const LINKREQUESTS_LOADING = linkRequestState.getConstants().LOADING;
export const LINKREQUESTS_LOADED = linkRequestState.getConstants().LOADED;
export const LINKREQUESTS = linkRequestState.getConstants().DATA;
export const LINKREQUEST_IS_PENDING = 'LINKREQUEST_IS_PENDING';
export const LINKREQUEST_IS_CREATED = 'LINKREQUEST_IS_CREATED';
export const LINKREQUEST_IS_DECLINED = 'LINKREQUEST_IS_DECLINED';

export const UPDATE_LINKREQUEST = DEPOT_STORE + '/' + 'UPDATE_LINKREQUEST';
export const LINKREQUEST_IS_SAVING = 'LINKREQUEST_IS_SAVING';
export const LINKREQUEST_SAVED = 'LINKREQUEST_SAVED';

export const FETCH_PLACEDORDERS = DEPOT_STORE + '/' + placedOrdersState.getConstants().FETCH;
export const PLACEDORDERS_LOADING = placedOrdersState.getConstants().LOADING;
export const PLACEDORDERS_LOADED = placedOrdersState.getConstants().LOADED;
export const PLACEDORDERS = placedOrdersState.getConstants().DATA;